import { zodResolver } from '@hookform/resolvers/zod';
import { EVENT_LABELS, EventType, WeatherCondition, type EntryFull } from '@siteday/shared';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { equipmentApi, errorMessage, isApiError, qk, usersApi } from '@/api';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input, Select, Textarea } from '@/components/ui/input';
import { Card, CardBody, CardHeader, Table } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { useImpersonation } from '@/features/auth/session';
import type { Snapshot } from '@/lib/diff';

import {
  EntryEditSchema,
  newLineId,
  toDocument,
  valuesFromEntry,
  type EntryEditInput,
  type EntryEditValues,
} from './editSchema';
import { usePatchEntry } from './useEntries';

/** W4 edit mode: same layout, editable; Save requires a reason; sends baseVersion; 409 → reload banner. */
export function EntryEditForm({
  entry,
  seed,
  onDone,
  onReload,
}: {
  entry: EntryFull;
  seed: Snapshot | null;
  onDone: () => void;
  onReload: () => void;
}) {
  const form = useForm<EntryEditInput, unknown, EntryEditValues>({
    resolver: zodResolver(EntryEditSchema),
    defaultValues: valuesFromEntry(entry, seed),
  });
  const crew = useFieldArray({ control: form.control, name: 'crewLines' });
  const equipment = useFieldArray({ control: form.control, name: 'equipmentLines' });
  const quantities = useFieldArray({ control: form.control, name: 'quantityLines' });
  const events = useFieldArray({ control: form.control, name: 'events' });
  const users = useQuery({
    queryKey: qk.users.list({ active: 'true' }),
    queryFn: () => usersApi.listUsers({ active: 'true' }),
  });
  const machines = useQuery({
    queryKey: qk.equipment.list({ active: 'true' }),
    queryFn: () => equipmentApi.listEquipment({ active: 'true' }),
  });
  const patch = usePatchEntry(entry.id);
  const toast = useToast();
  const { impersonating } = useImpersonation();
  const [stale, setStale] = useState(false);
  const [addCrew, setAddCrew] = useState('');
  const [addMachine, setAddMachine] = useState('');
  const errors = form.formState.errors;

  const onSubmit = form.handleSubmit((v) => {
    patch.mutate(
      { reason: v.reason, baseVersion: entry.version, document: toDocument(entry, v) },
      {
        onSuccess: () => {
          toast.success(`Saved as v${entry.version + 1}`);
          onDone();
        },
        onError: (e) => {
          if (isApiError(e) && (e.code === 'ENTRY_VERSION_STALE' || e.status === 409))
            setStale(true);
          else toast.error(errorMessage(e));
        },
      },
    );
  });

  const crewIds = new Set(form.watch('crewLines').map((l) => l.userId));
  const machineIds = new Set(form.watch('equipmentLines').map((l) => l.equipmentId));

  return (
    <form onSubmit={onSubmit} noValidate data-testid="entry-edit-form">
      {stale ? (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-bad bg-hi-bad px-4 py-3 text-[13px]"
        >
          <b className="text-bad">Someone changed this entry — reload.</b>
          <span className="text-ink2">
            Your edits were not saved. Reload to see the newer version, then make the change again.
          </span>
          <Button size="sm" variant="dark" className="ml-auto" onClick={onReload}>
            Reload
          </Button>
        </div>
      ) : null}
      {seed ? (
        <div className="mb-4 rounded-xl border border-orange-bg bg-hi px-4 py-2.5 text-[13px] text-ink2">
          Prefilled with the field version. Saving resolves the conflict with this merged document.
        </div>
      ) : null}

      <div className="three">
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader title="Quantities">
              <div className="ml-auto flex items-center gap-2">
                <Select
                  aria-label="Weather"
                  className="h-7 w-auto text-xs"
                  {...form.register('weatherCondition')}
                >
                  <option value="">Weather…</option>
                  {WeatherCondition.options.map((w) => (
                    <option key={w} value={w}>
                      {w[0]!.toUpperCase() + w.slice(1)}
                    </option>
                  ))}
                </Select>
                <Input
                  aria-label="Temperature °F"
                  className="h-7 w-16 text-xs"
                  placeholder="°F"
                  inputMode="numeric"
                  invalid={!!errors.temperatureF}
                  {...form.register('temperatureF')}
                />
              </div>
            </CardHeader>
            <Table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th className="r">Today</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                {quantities.fields.map((q, i) => (
                  <tr key={q.id}>
                    <td>
                      <code>{q.code}</code>
                    </td>
                    <td>{q.description}</td>
                    <td className="r">
                      <Input
                        aria-label={`Quantity for ${q.code}`}
                        className="h-7 w-24 text-right"
                        inputMode="decimal"
                        invalid={!!errors.quantityLines?.[i]?.quantity}
                        {...form.register(`quantityLines.${i}.quantity`)}
                      />
                    </td>
                    <td>{q.unit}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <div className="two">
            <Card>
              <CardHeader title="Crew" />
              <Table>
                <tbody>
                  {crew.fields.map((l, i) => (
                    <tr key={l.id}>
                      <td>{l.userName}</td>
                      <td className="r">
                        <Input
                          aria-label={`Hours for ${l.userName}`}
                          className="h-7 w-20 text-right"
                          inputMode="decimal"
                          invalid={!!errors.crewLines?.[i]?.hours}
                          {...form.register(`crewLines.${i}.hours`)}
                        />
                      </td>
                      <td className="act">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove ${l.userName}`}
                          onClick={() => crew.remove(i)}
                        >
                          <Trash2 />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <CardBody className="flex gap-2 border-t border-line2">
                <Select
                  aria-label="Add crew member"
                  value={addCrew}
                  onChange={(e) => setAddCrew(e.target.value)}
                  className="h-8"
                >
                  <option value="">Add person…</option>
                  {users.data?.items
                    .filter((u) => !crewIds.has(u.id) && u.role !== 'owner' && u.role !== 'admin')
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                </Select>
                <Button
                  size="sm"
                  disabled={!addCrew}
                  onClick={() => {
                    const u = users.data?.items.find((x) => x.id === addCrew);
                    if (!u) return;
                    crew.append({ id: newLineId(), userId: u.id, userName: u.name, hours: '10' });
                    setAddCrew('');
                  }}
                >
                  <Plus /> Add
                </Button>
              </CardBody>
            </Card>
            <Card>
              <CardHeader title="Equipment" />
              <Table>
                <tbody>
                  {equipment.fields.map((l, i) => (
                    <tr key={l.id}>
                      <td>{l.equipmentName}</td>
                      <td className="r">
                        <Input
                          aria-label={`Hours for ${l.equipmentName}`}
                          className="h-7 w-20 text-right"
                          inputMode="decimal"
                          invalid={!!errors.equipmentLines?.[i]?.hours}
                          {...form.register(`equipmentLines.${i}.hours`)}
                        />
                      </td>
                      <td className="act">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove ${l.equipmentName}`}
                          onClick={() => equipment.remove(i)}
                        >
                          <Trash2 />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <CardBody className="flex gap-2 border-t border-line2">
                <Select
                  aria-label="Add machine"
                  value={addMachine}
                  onChange={(e) => setAddMachine(e.target.value)}
                  className="h-8"
                >
                  <option value="">Add machine…</option>
                  {machines.data?.items
                    .filter((m) => !machineIds.has(m.id))
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} {m.unitNumber ? `· ${m.unitNumber}` : ''}
                      </option>
                    ))}
                </Select>
                <Button
                  size="sm"
                  disabled={!addMachine}
                  onClick={() => {
                    const m = machines.data?.items.find((x) => x.id === addMachine);
                    if (!m) return;
                    equipment.append({
                      id: newLineId(),
                      equipmentId: m.id,
                      equipmentName: m.name,
                      hours: '10',
                    });
                    setAddMachine('');
                  }}
                >
                  <Plus /> Add
                </Button>
              </CardBody>
              <CardHeader title="Delays & extra work" className="border-t border-line2" />
              <CardBody className="flex flex-col gap-2">
                {events.fields.map((e, i) => (
                  <div key={e.id} className="flex flex-wrap items-center gap-2">
                    <Select
                      aria-label="Event type"
                      className="h-8 w-auto"
                      {...form.register(`events.${i}.type`)}
                    >
                      {EventType.options.map((t) => (
                        <option key={t} value={t}>
                          {EVENT_LABELS[t]}
                        </option>
                      ))}
                    </Select>
                    <Input
                      aria-label="Minutes"
                      className="h-8 w-20"
                      placeholder="min"
                      inputMode="numeric"
                      invalid={!!errors.events?.[i]?.durationMinutes}
                      {...form.register(`events.${i}.durationMinutes`)}
                    />
                    <Input
                      aria-label="Description"
                      className="h-8 min-w-40 flex-1"
                      placeholder="What happened"
                      {...form.register(`events.${i}.description`)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Remove event"
                      onClick={() => events.remove(i)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  className="self-start"
                  onClick={() =>
                    events.append({
                      id: newLineId(),
                      type: 'delay_weather',
                      durationMinutes: '',
                      description: '',
                    })
                  }
                >
                  <Plus /> Add delay or extra work
                </Button>
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader title="Notes" />
            <CardBody>
              <Textarea aria-label="Notes" rows={4} {...form.register('notes')} />
            </CardBody>
          </Card>
        </div>

        <Card className="self-start">
          <CardHeader title="Save changes" />
          <CardBody className="flex flex-col gap-3">
            <Field
              label="Reason for the change"
              htmlFor="edit-reason"
              error={errors.reason?.message}
              hint="Required. Stored with the revision and visible to the foreman."
            >
              <Textarea
                id="edit-reason"
                rows={3}
                placeholder='e.g. "Tony confirmed by phone — Danny left at 4:30"'
                invalid={!!errors.reason}
                {...form.register('reason')}
              />
            </Field>
            <p className="text-xs text-ink3">
              Saving creates <b>v{entry.version + 1}</b> from v{entry.version}. The field version is
              never overwritten; every version keeps a full snapshot and the PDF regenerates.
              {entry.status === 'approved' || entry.status === 'synced'
                ? ' The day stays approved; the change goes to QuickBooks as a correction on the next export run.'
                : ''}
            </p>
            <div className="flex justify-end gap-2">
              <Button onClick={onDone}>Cancel</Button>
              <Button
                type="submit"
                variant="primary"
                loading={patch.isPending}
                disabled={impersonating}
                data-testid="save-entry"
              >
                Save v{entry.version + 1}
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </form>
  );
}
